// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  accountSettingLoginPath,
  apiPath,
  dashboardPath,
} from 'presentations/routes/path';
import { useRequest } from 'presentations/views/hooks/request';
import { isInElectron } from 'presentations/views/helpers/electron';
import { useRouter } from 'next/router';
import {
  DashboardPortalContext,
  // setIsOpenUserInviteForm,
} from 'presentations/reducers/DashboardPortal';
import { Option } from 'presentations/views/components/atoms/Select';
import {
  appearanceCacheKey,
  Appearance,
} from 'presentations/views/styles/color';
import {
  defaultCacheExpiration,
  getPersistentItem,
  setPersistentItem,
} from 'presentations/views/helpers/storage';
import { setEvent } from 'presentations/views/helpers/analytics';
import { BlogListItem } from 'presentations/api/schema/contents';

interface Props {
  viewer?: ViewerItem;
}
import { MenuOptions } from 'presentations/views/components/molecules/FloatSettingMenu';
import { ViewerItem } from 'presentations/api/schema/user';

export function useAccountMenuPage(props: Props) {
  const t = translateInComponent(translations);
  const { state, dispatch } = useContext(DashboardPortalContext);

  const [isElectron, setIsElectron] = useState<boolean>(false);
  const [appearance, setAppearance] = useState<Appearance>(Appearance.Normal);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const { state: dashboardState } = useContext(DashboardPortalContext);
  const [
    accountMenuAnchor,
    setAccountMenuAnchor,
  ] = useState<null | HTMLElement>(null);
  const viewer = props.viewer || dashboardState.viewer;

  const router = useRouter();

  useEffect(() => {
    setIsElectron(isInElectron());
    const value = getPersistentItem(appearanceCacheKey);
    setAppearance(
      String(value) === Appearance.Dark ? Appearance.Dark : Appearance.Normal,
    );
  }, []);

  const blogsRequest = useRequest(
    apiPath(`/v1/contents/blogs`, {
      page: 1,
      perPage: 5,
      category: 'news',
      locale: router.locale,
    }),
  );
  const blogs: BlogListItem[] = blogsRequest.data || [];

  const onChangeLocale = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => {
      router.replace(dashboardPath(), undefined, {
        locale: e.currentTarget.value,
      });
      setEvent('locale change', {
        locale: e.currentTarget.value,
      });
    },
    [router],
  );
  const appearanceOptions: Option[] = [
    {
      text: t('Light'),
      value: Appearance.Normal,
    },
    {
      text: t('Dark'),
      value: Appearance.Dark,
    },
  ];
  const onChangeAppearance = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => {
      const a =
        e.currentTarget.value === Appearance.Dark
          ? Appearance.Dark
          : Appearance.Normal;
      setAppearance(a);
      setPersistentItem(
        appearanceCacheKey,
        a,
        defaultCacheExpiration * 12 * 100,
      );
      setEvent('appearance change', {
        appearance: a,
      });
      window.location.reload();
    },
    [],
  );

  const onClickMenu = (e: SyntheticEvent<HTMLInputElement>) => {
    setAnchor(e.currentTarget);
  };

  const onCloseMenu = () => {
    setAnchor(null);
  };

  const onSelectMenu = async (selectedIndex: number) => {
    if (selectedIndex === 0) {
      router.push(accountSettingLoginPath());
    }
  };

  const onClickAccountMenu = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAccountMenuAnchor(e.currentTarget);
  };

  const onCloseAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const onSelectAccountMenu = (selectedIndex: number) => {
    setAccountMenuAnchor(null);
    switch (selectedIndex) {
      case 0:
        router.push(accountSettingLoginPath());
        break;
      default:
        break;
    }
  };

  const options: MenuOptions[] = [
    {
      label: t('Login'),
      icon: 'settings',
    },
  ];

  return {
    state: {
      isElectron,
      appearanceOptions,
      appearance,
      blogs,
      anchor,
      options,
      viewer,
      accountMenuAnchor,
    },
    actions: {
      onChangeAppearance,
      onChangeLocale,
      onCloseMenu,
      onSelectMenu,
      onClickMenu,
      onClickAccountMenu,
      onCloseAccountMenu,
      onSelectAccountMenu,
    },
  };
}
